import cloneDeep from 'lodash/cloneDeep'
import { defineStore } from 'pinia'
import { reactive } from 'vue'
import { EFilterNames, ESortValues, type ICarSorting, type IPaginationState } from '../types/filters'

type Nullable<T> = T | null

export interface ICarsStoreState {
	cars: Nullable<IProductModel[]>
	filteredCars: Nullable<IProductModel[]>
	allFilters: Nullable<IProductParameterModel[]>
	allSegments: Nullable<IProductSegmentModel[]>
	allGroups: Nullable<IProductGroupModel[]>
	currentFilters: Nullable<ICarSelectedFilters[]>
	currentSorting: Nullable<ICarSorting>
	currentGroups: Nullable<IProductGroupModel[]>
	currentPriceRange: Nullable<IPriceRange>
	carsLoading: boolean
	appliedFilters: IProductParameterSearch[]
	pagination: Nullable<IPaginationState>
	segmentCars: Record<number, IProductModel[]>
}

export interface ICarsStore {
	readonly state: {
		cars: Nullable<IProductModel[]>
		filteredCars: Nullable<IProductModel[]>
		allFilters: Nullable<IProductParameterModel[]>
		allSegments: Nullable<IProductSegmentModel[]>
		allGroups: Nullable<IProductGroupModel[]>
		currentFilters: Nullable<ICarSelectedFilters[]>
		currentGroups: Nullable<IProductGroupModel[]>
		currentPriceRange: Nullable<IPriceRange>
		carsLoading: boolean
		appliedFilters: IProductParameterSearch[]
	}
	setCar: (product: IProductModel) => void
	setFilter: (filter: IProductParameterModel) => void
	fetchAllFilters: () => Promise<IProductParameterModel[]>
	fetchAllSegments: () => Promise<IProductSegmentModel[]>
	fetchAllGroups: () => Promise<IProductGroupModel[]>
	fetchSegmentCars: (segmentID: number) => Promise<IProductModel[]>
	fetchCarsWithFilters: (options?: {
		signal?: AbortSignal
		searches?: IProductParameterSearch[]
		filters?: IProductFilter[]
		sort?: string
		page?: number
		perPage?: number
	}) => Promise<{ response: IProductModel[]; links: any }>
	setFilteredCars: (cars: IProductModel[]) => void
	setCurrentFilter: (filter: IProductParameterModel, values?: string[] | number[], min?: number) => void
	setCurrentSorting: (sort: ICarSorting | null) => void
	setCurrentFilterByValue: (filter: IProductParameterModel, value?: string | number, min?: number) => void
	setCurrentGroup: (group: IProductGroupModel) => void
	setCurrentPriceRange: (range: IPriceRange) => void
	getFilterById: (id: string) => IProductParameterModel | undefined
	clearFilters: () => void
	deleteFilterValue: (filter: ICarSelectedFilters, value: string | number) => void
	deleteCurrentFilter: (filter: ICarSelectedFilters) => void
	getCurrentFilterById: (id: string | number) => ICarSelectedFilters | undefined
	getCars: ComputedRef<Nullable<IProductModel[]>>
	getFilteredCars: ComputedRef<Nullable<IProductModel[]>>
	getCurrentFilters: ComputedRef<Nullable<ICarSelectedFilters[]>>
	getAllSegments: ComputedRef<Nullable<IProductSegmentModel[]>>
	getCarsLoading: ComputedRef<boolean>
	getCurrentSorting: ComputedRef<Nullable<ICarSorting>>
	getAllGroups: ComputedRef<Nullable<IProductGroupModel[]>>
	getCurrentGroups: ComputedRef<Nullable<IProductGroupModel[]>>
	getCurrentPriceRange: ComputedRef<Nullable<IPriceRange>>
	getPagination: ComputedRef<Nullable<IPaginationState>>
	getFilterByParameterName: (name: EFilterNames) => IProductParameterModel | undefined
}

export const useCarsStore = defineStore('carsStore', (): ICarsStore => {
	const _state = reactive<ICarsStoreState>({
		cars: [],
		filteredCars: [],
		allFilters: [],
		currentFilters: [],
		allSegments: [],
		allGroups: [],
		currentGroups: [],
		currentSorting: null,
		currentPriceRange: null,
		carsLoading: false,
		appliedFilters: [],
		pagination: null,
		segmentCars: {},
	})

	let fetchAllGroupsPromise: Nullable<Promise<IProductGroupModel[]>> = null
	const fetchSegmentCarsPromises = new Map<number, Promise<IProductModel[]>>()

	const setCarsLoading = (loading: boolean) => {
		_state.carsLoading = loading
	}

	const setCar = (product: IProductModel) => {
		if (_state.cars?.find((p) => p.id === product.id)) return
		_state.cars?.push(product)
	}

	const setFilter = (filter: IProductParameterModel) => {
		if (_state.allFilters?.find((f) => f.id === filter.id)) return
		_state.allFilters?.push(filter)
	}

	const setFilteredCars = (cars: IProductModel[]) => {
		_state.filteredCars = cars
	}

	const setCurrentSorting = (sort: { name: string; value: ESortValues } | null) => {
		_state.currentSorting = sort
	}

	const fetchAllSegments = async () => {
		if (_state.allSegments && _state.allSegments.length > 0) return _state.allSegments

		const data = await ApiService.fetchSegments()

		_state.allSegments = data

		return data
	}

	const fetchAllGroups = async () => {
		if (_state.allGroups && _state.allGroups.length > 0) {
			return _state.allGroups
		}

		if (fetchAllGroupsPromise) {
			return fetchAllGroupsPromise
		}

		// const data = await ApiService.fetchAllGroups()

		fetchAllGroupsPromise = (async () => {
			const fetchedGroups = await ApiService.fetchAllGroups()

			if (fetchedGroups.data && fetchedGroups.data.length > 0) {
				_state.allGroups = fetchedGroups.data
			} else {
				_state.allGroups = []
			}

			return _state.allGroups as IProductGroupModel[]
		})()

		return await fetchAllGroupsPromise
		// _state.allGroups = data.data
		// _state.allGroups = data.data.filter((group) => group.name !== 'electric' && group.name !== 'automatic')

		// return data.data
	}

	const SEGMENT_CARS_LIMIT = 4

	const fetchSegmentCars = async (segmentID: number) => {
		if (_state.segmentCars[segmentID]) return _state.segmentCars[segmentID]

		const pending = fetchSegmentCarsPromises.get(segmentID)
		if (pending) return pending

		const request = (async () => {
			try {
				const searches = [{ id: 'segment_id', values: [segmentID] }]
				const { response } = await ApiService.fetchProductsByParameters(searches, undefined, 1, SEGMENT_CARS_LIMIT, undefined, undefined, {
					onlyVisible: false,
				})
				_state.segmentCars[segmentID] = response
				return response
			} finally {
				fetchSegmentCarsPromises.delete(segmentID)
			}
		})()

		fetchSegmentCarsPromises.set(segmentID, request)
		return await request
	}

	// const fetchLocationCities = async () => {
	// 	try {
	// 		if (_state.cities && _state.cities.length > 0) return _state.cities

	// 		if (_state.fetchLocationCitiesPromise) return _state.fetchLocationCitiesPromise

	// 		_state.fetchLocationCitiesPromise = (async () => {
	// 			const fetchedCities = await ApiService.fetchAllLocationCities()

	// 			if (fetchedCities && fetchedCities.length > 0) {
	// 				_state.cities = fetchedCities
	// 			} else {
	// 				_state.cities = []
	// 			}

	// 			return _state.cities as ILocationCityModel[]
	// 		})()

	// 		return await _state.fetchLocationCitiesPromise
	// 	} catch (error) {
	// 		console.log(error)
	// 	}
	// }

	const fetchCarsWithFilters = async (options?: {
		signal?: AbortSignal
		skipSSRPayload?: boolean
		searches?: IProductParameterSearch[]
		filters?: IProductFilter[]
		sort?: string
		page?: number
		perPage?: number
	}) => {
		setCarsLoading(true)

		const searches = options?.searches ?? []
		const filters = options?.filters ?? []
		const page = options?.page ?? 1
		const perPage = options?.perPage ?? 10
		const sort = options?.sort ?? undefined

		try {
			const { response, links, meta } = await ApiService.fetchProductsByParameters(searches, filters, page, perPage, sort, undefined, {
				signal: options?.signal,
				skipSSRPayload: options?.skipSSRPayload ?? false,
			})

			if (options?.signal?.aborted) return { response: [], links: undefined }

			_state.filteredCars = response
			if (meta) {
				const m = meta as any
				_state.pagination = {
					currentPage: m.current_page ?? m.currentPage ?? 1,
					totalPages: m.last_page ?? m.lastPage ?? 1,
					total: m.total ?? 0,
					perPage: m.per_page ?? m.perPage ?? 10,
				}
			}

			setCarsLoading(false)
			getChangedFilters(searches)
			return { response, links }
		} catch (err) {
			if (!options?.signal?.aborted) setCarsLoading(false)
			throw err
		}
	}

	const fetchAllFilters = async () => {
		if (_state.allFilters && _state.allFilters.length > 0) return _state.allFilters

		const data = await ApiService.fetchParameters()

		data.forEach((filter) => {
			setFilter(filter)
		})

		return data
	}

	const getFilterByParameterName = (name: EFilterNames) => {
		return _state.allFilters?.find((filter) => filter.name === name)
	}

	const removeFilterByIndex = (index: number) => {
		if (!_state.currentFilters) return
		_state.currentFilters.splice(index, 1)
	}

	const setFilterByIndex = (index: number, values?: string[] | number[], min?: number) => {
		if (!_state.currentFilters) return
		// if values are empty, remove filter
		if (!values || (values?.length === 0 && !min)) {
			removeFilterByIndex(index)
			return
		}

		values ? (_state.currentFilters[index].values = values) : (_state.currentFilters[index].min = min)

		// _state.currentFilters[index].values = values
	}

	const setFilterValueByIndex = (index: number, value: string | number) => {
		if (!_state.currentFilters) return

		// check if vlaue is already set
		const valueIndex = _state.currentFilters[index].values.findIndex((v) => v === value)

		// if value is already set, check if it is last value and if it is, remove filter or remove only value
		if (valueIndex !== -1) {
			if (_state.currentFilters[index].values.length === 1) {
				removeFilterByIndex(index)
				return
			}
			_state.currentFilters[index].values.splice(valueIndex, 1)
			return
		}

		// if value is not set, add it

		_state.currentFilters[index].values.push(value as never)
	}

	const setCurrentFilter = (filter: IProductParameterModel, values?: string[] | number[], min?: number) => {
		const filterIndex = _state.currentFilters?.findIndex((f) => f.parameter.id === filter.id)
		// if filter is already set, update its values
		if (filterIndex !== -1) {
			setFilterByIndex(filterIndex as number, values, min)
		} else {
			// if filter is not set, add it
			_state.currentFilters?.push({ parameter: filter, values: values || [], min })
		}
	}

	const setCurrentFilterByValue = (filter: IProductParameterModel, value: string | number) => {
		const filterIndex = _state.currentFilters?.findIndex((f) => f.parameter.id === filter.id)

		if (filterIndex !== -1) {
			setFilterValueByIndex(filterIndex as number, value)
		} else {
			_state.currentFilters?.push({ parameter: filter, values: [value] as string[] | number[] })
		}

		// if values are empty, remove filter
		if (_state.currentFilters) {
			const filterIndex = _state.currentFilters.findIndex((f) => f.parameter.id === filter.id)
			if (filterIndex !== -1) {
				if (_state.currentFilters[filterIndex].values.length === 0) {
					removeFilterByIndex(filterIndex)
				}
			}
		}
	}

	const setCurrentGroup = (group: IProductGroupModel) => {
		// check if group is already set
		const groupIndex = _state.currentGroups?.findIndex((g) => g.id === group.id)
		// if group is already set, remove it
		if (groupIndex !== -1) {
			_state.currentGroups?.splice(groupIndex as number, 1)
			return
		}
		// if group is not set, add it
		_state.currentGroups?.push(group)
	}

	const setCurrentPriceRange = (range: IPriceRange) => {
		// check if range is already set and if it the same
		if (_state.currentPriceRange && _state.currentPriceRange.min === range.min && _state.currentPriceRange.max === range.max) {
			_state.currentPriceRange = null
		} else {
			_state.currentPriceRange = range
		}
	}

	const getFilterById = (id: string) => {
		return _state.allFilters?.find((filter) => filter.id === id)
	}

	const clearFilters = () => {
		_state.currentFilters = []
		_state.currentGroups = []
		_state.currentPriceRange = null
		sendAnalyticsData('clear')
	}

	const deleteFilterValue = (filter: ICarSelectedFilters, value: string | number) => {
		if (filter.values.length === 1) {
			const filterIndex = _state.currentFilters?.findIndex((f) => f.parameter.id === filter.parameter.id)
			if (filterIndex !== -1) {
				removeFilterByIndex(filterIndex as number)
			}
			return
		}

		const valueIndex = filter.values.findIndex((v) => v === value)
		filter.values.splice(valueIndex, 1)
	}

	const deleteCurrentFilter = (filter: ICarSelectedFilters) => {
		const filterIndex = _state.currentFilters?.findIndex((f) => f.parameter.id === filter.parameter.id)
		if (filterIndex !== -1) {
			removeFilterByIndex(filterIndex as number)
		}
	}

	const getCurrentFilters = computed(() => {
		return _state.currentFilters
	})

	const getCurrentFilterById = (id: string | number) => {
		return _state.currentFilters?.find((f) => f.parameter.id === id)
	}

	const getCars = computed(() => {
		return _state.cars
	})

	const getFilteredCars = computed(() => _state.filteredCars)

	const getAllSegments = computed(() => {
		return _state.allSegments
	})

	const getCarsLoading = computed(() => {
		return _state.carsLoading
	})

	const getCurrentSorting = computed(() => {
		return _state.currentSorting
	})

	const getAllGroups = computed(() => {
		return _state.allGroups
	})

	const getCurrentGroups = computed(() => {
		return _state.currentGroups
	})

	const getPagination = computed(() => _state.pagination)

	const getCurrentPriceRange = computed(() => {
		return _state.currentPriceRange
	})

	const getChangedFilters = (filters: IProductParameterSearch[]) => {
		const prevFilters = _state.appliedFilters || []
		// Find added and removed filters
		const addedFilters: { id: string | number; value: any }[] = []
		const removedFilters: { id: string | number; value: any }[] = []
		// Check for added values in filters
		filters.forEach((filter) => {
			const prevFilter = prevFilters.find((prev) => prev.id === filter.id)
			if (!prevFilter) {
				// Entire filter is new
				if (filter.values) {
					filter.values.forEach((value) => addedFilters.push({ id: filter.id, value }))
				} else if (filter.min || filter.max) {
					if (filter.id === 'price') {
						// different approaches for mobile filters and price isnt unified so > 9999
						const min = filter.min || filter.min === 0 ? (filter.min > 9999 ? filter.min / 100 : filter.min) : 0
						const max = filter.max || filter.max === 0 ? (filter.max > 9999 ? filter.max / 100 : filter.max) : 0
						addedFilters.push({
							id: filter.id,
							value: max ? `${min}-${max}` : `${min}`,
						})
					} // seats
					if (filter.id === 7) addedFilters.push({ id: filter.id, value: filter.min })
				}
			} else if (filter.values) {
				// Check for new values in existing filter
				filter.values.forEach((value) => {
					if (!prevFilter.values?.includes(value)) {
						addedFilters.push({ id: filter.id, value })
					}
				})
			} else if (filter.min || filter.max) {
				if (prevFilter.min !== filter.min || prevFilter.max !== filter.max) {
					if (filter.id === 'price') {
						const previousMin =
							prevFilter.min || prevFilter.min === 0 ? (prevFilter.min > 9999 ? prevFilter.min / 100 : prevFilter.min) : 0
						const previousMax =
							prevFilter.max || prevFilter.max === 0 ? (prevFilter.max > 9999 ? prevFilter.max / 100 : prevFilter.max) : 0
						const min = filter.min || filter.min === 0 ? (filter.min > 9999 ? filter.min / 100 : filter.min) : 0
						const max = filter.max || filter.max === 0 ? (filter.max > 9999 ? filter.max / 100 : filter.max) : 0
						removedFilters.push({
							id: prevFilter.id,
							value: previousMax ? `${previousMin}-${previousMax}` : `${previousMin}`,
						})
						addedFilters.push({ id: filter.id, value: max ? `${min}-${max}` : `${min}` })
					}
					if (filter.id === 7) {
						removedFilters.push({ id: prevFilter.id, value: prevFilter.min })
						addedFilters.push({ id: filter.id, value: filter.min })
					}
				}
			}
		})
		// Check for removed values in filters
		prevFilters.forEach((prevFilter) => {
			const currentFilter = filters.find((filter) => filter.id === prevFilter.id)
			if (!currentFilter) {
				// Entire filter removed
				if (prevFilter.values) {
					prevFilter.values.forEach((value) => removedFilters.push({ id: prevFilter.id, value }))
				} else if (prevFilter.id === 7) {
					removedFilters.push({ id: prevFilter.id, value: prevFilter.min })
				} else if (prevFilter.id === 'price') {
					const previousMin = prevFilter.min || prevFilter.min === 0 ? (prevFilter.min > 9999 ? prevFilter.min / 100 : prevFilter.min) : 0
					const previousMax = prevFilter.max || prevFilter.max === 0 ? (prevFilter.max > 9999 ? prevFilter.max / 100 : prevFilter.max) : 0
					removedFilters.push({
						id: prevFilter.id,
						value: previousMax ? `${previousMin}-${previousMax}` : `${previousMin}`,
					})
				}
			} else if (prevFilter.values) {
				// Check for removed values in existing filter
				prevFilter.values.forEach((value) => {
					if (!currentFilter.values?.includes(value)) {
						removedFilters.push({ id: prevFilter.id, value })
					}
				})
			}
		})
		// Send analytics for changes
		if (addedFilters.length > 0 || removedFilters.length > 0) {
			sendAnalyticsData({ addedFilters, removedFilters })
		}
		// Update applied filters state
		_state.appliedFilters = cloneDeep(filters)
	}
	const gtmTimeoutForClear = ref(false)
	const sendAnalyticsData = (filters?: any) => {
		const gtmService = useGTMService()
		if (!gtmService) return
		if (gtmTimeoutForClear.value) return
		if (filters === 'clear') {
			gtmTimeoutForClear.value = true
			setTimeout(() => {
				gtmTimeoutForClear.value = false
			}, 1000)
		}
		const data = {
			addedFilters: filters?.addedFilters || [],
			removedFilters: filters?.removedFilters || [],
		}
		data.removedFilters.forEach((filter: { id: string; value: any }) => {
			const gtmParameters = {
				results_number: _state.filteredCars?.length,
				filter_name: getFilterName(filter.id),
				interaction_type: 'remove',
				element_value: filter.id === 'group_id' ? _state.allGroups?.find((g) => g.id === filter.value)?.name : filter.value,
			}
			gtmService.listingFiltersInteraction(gtmParameters)
		})
		data.addedFilters.forEach((filter: { id: string; value: any }) => {
			const gtmParameters = {
				results_number: _state.filteredCars?.length,
				filter_name: getFilterName(filter.id),
				interaction_type: 'apply',
				element_value: filter.id === 'group_id' ? _state.allGroups?.find((g) => g.id === filter.value)?.name : filter.value,
			}
			gtmService.listingFiltersInteraction(gtmParameters)
		})
		if (filters === 'clear') {
			const gtmParameters = {
				results_number: _state.filteredCars?.length,
				filter_name: null,
				interaction_type: 'remove',
				element_value: 'all',
			}
			gtmService.listingFiltersInteraction(gtmParameters)
		}
	}
	const getFilterName = (id: string | number) => {
		switch (id) {
			case 'price':
				return 'price_per_day'
			case 'group_id':
				return 'car_category'
			case 8:
				return 'gearbox'
			case 7:
				return 'seats'
			case 3:
				return 'engine'
			default:
				return null
		}
	}

	return {
		state: _state,
		setCar,
		setFilter,
		fetchCarsWithFilters,
		setFilteredCars,
		setCurrentFilter,
		setCurrentFilterByValue,
		setCurrentGroup,
		setCurrentPriceRange,
		setCurrentSorting,
		getFilterById,
		clearFilters,
		deleteFilterValue,
		deleteCurrentFilter,
		getCurrentFilterById,
		getCars,
		getFilteredCars,
		getCurrentSorting,
		getCurrentFilters,
		getAllSegments,
		getFilterByParameterName,
		fetchAllFilters,
		fetchAllSegments,
		fetchAllGroups,
		fetchSegmentCars,
		getCarsLoading,
		getAllGroups,
		getCurrentGroups,
		getCurrentPriceRange,
		getPagination,
	}
})
